import { Component, OnInit, inject, effect, untracked, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import { TournamentService } from '../../services';
import { Round, Tournament } from '../../models';

import { LoggingService } from '../../services/shared/logging/logging.service';
import { ScoringRealtimeService, TournamentActivityData } from '../../services/signalr/scoring-realtime.service';
import { LayoutService } from '../../services/shared/layout/layout.service';

@Component({
  selector: 'app-navtop',
  standalone: true,
  imports: [],
  templateUrl: './navtop.component.html',
  styleUrls: ['./navtop.component.scss'],
})
export class NavtopComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly tournamentService = inject(TournamentService);
  private readonly loggingService = inject(LoggingService);
  private readonly scoringRealtimeService = inject(ScoringRealtimeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);

  // Reactive state using signals
  tournaments = signal<Tournament[]>([]);
  rounds = signal<Round[]>([]);

  // Convert real-time observables to signals
  tournamentEvents = toSignal(this.scoringRealtimeService.tournamentActivity, { initialValue: null });

  // Track current URL reactively
  currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  // Computed properties for UI
  selectedTournament = this.tournamentService.selectedTournamentSignal;
  selectedRound = this.tournamentService.selectedRoundSignal;
  isAuthenticated = computed(() => this.authService.isAuthenticated());

  // Hide round selector on tournaments list page
  isOnTournamentsPage = computed(() => this.currentUrl() === '/tournaments');

  // Sidebar collapse state mirrors INSPINIA behaviour
  sidebarCollapsed = this.layoutService.isSidebarCollapsed;

  constructor() {
    // Set up reactive tournament event handling
    this.setupTournamentEventHandling();
  }

  ngOnInit() {
    if (!this.isAuthenticated()) {
      return;
    }
    this.loadTournaments();
  }

  private setupTournamentEventHandling(): void {
    effect(() => {
      const event = this.tournamentEvents();
      if (event) {
        this.loggingService.debug('NavtopComponent', 'Tournament event received:', event);
        untracked(() => {
          this.handleTournamentEvent(event);
        });
      }
    });
  }

  private loadTournaments() {
    this.tournamentService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tournaments) => {
          this.tournaments.set(tournaments);
          const activeTournament = tournaments.find((t) => t.active) || null;
          if (activeTournament) {
            this.selectTournament(activeTournament);
          } else if (tournaments.length > 0) {
            this.selectTournament(tournaments[0]);
          }
        },
        error: (error) => {
          this.loggingService.error('NavtopComponent', 'Error fetching tournaments:', error);
        },
      });
  }

  selectTournament(tournament: Tournament) {
    this.tournamentService.selectedTournament = tournament;

    // Navigate to the tournament overview page
    this.router.navigate(['/tournaments', tournament.tournamentId]);

    this.tournamentService.getRounds(tournament.tournamentId!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rounds) => {
          this.rounds.set(rounds);
          if (rounds.length > 0) {
            this.selectRound(rounds.find((r) => r.active) || rounds[0]);
          } else {
            this.tournamentService.selectedRound = null;
          }
        },
        error: (error) => {
          this.loggingService.error('Error fetching rounds:', error);
          this.rounds.set([]);
          this.tournamentService.selectedRound = null;
        },
      });
  }

  selectRound(round: Round) {
    this.tournamentService.selectedRound = round;
  }

  logout() {
    this.authService.logout();
  }

  private handleTournamentEvent(event: TournamentActivityData): void {
    if (event.type === 'TournamentMarkedActive' || event.type === 'TournamentMarkedNotActive') {
      this.refreshTournaments();
    }
  }

  private refreshTournaments(): void {
    const currentSelectedId = this.selectedTournament()?.tournamentId;

    this.tournamentService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tournaments) => {
          this.tournaments.set(tournaments);

          // Try to maintain current selection, otherwise select the active tournament
          let tournamentToSelect: Tournament | null = null;

          if (currentSelectedId) {
            tournamentToSelect = tournaments.find(t => t.tournamentId === currentSelectedId) || null;
          }

          if (!tournamentToSelect) {
            tournamentToSelect = tournaments.find(t => t.active) || tournaments[0] || null;
          }

          if (tournamentToSelect) {
            this.selectTournament(tournamentToSelect);
          }

          this.loggingService.debug('NavtopComponent', 'Tournaments refreshed after tournament event');
        },
        error: (error) => {
          this.loggingService.error('NavtopComponent', 'Error refreshing tournaments after event:', error);
        },
      });
  }

  toggleSidebar(): void {
    this.layoutService.toggleSidebar();
  }
}
